import { ActionType } from 'deox';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest, select } from 'redux-saga/effects';
import { User } from 'services/api';

import {
  getUserInfo,
  setCountry,
  setCurrency,
  updateUserInfo,
  updateUserPassword,
  getCreditCardInfo,
  createCreditCardInfo,
  deleteCreditCardInfo, getClickCountTotal, getCountries,
} from './actions';
import { RootState } from 'types';
import { processRequestError } from 'modules/error/actions';
import { setIsLocationCheck, setNotification } from 'modules/app/actions';

import texts from './texts';
import { t } from 'utils';
import {CountriesListProps} from "./types";

function* getUserInfoSaga() {
  try {
    const { data } = yield call(User.getUserInfo);
    if(data && data.country) {
      yield put(setCurrency(data.country[0].curr_code));
    }
    yield put(getUserInfo.success(data));
  } catch (error) {
    yield put(getUserInfo.fail(error));
  }
}

function* updateUserInfoSaga() {
  try {
    const { userInfo, countriesList } = yield select((state: RootState) => state.user);
    const { currentCountry } = yield select((state: RootState) => state.app);
    const findCountry = countriesList.filter((i: CountriesListProps)=> i.short_name.toLowerCase() === currentCountry.toLowerCase());
    const country = findCountry && findCountry.length ? findCountry[0]: null;
    const body: any = {};
    body.full_name = userInfo.fullName;
    body.email = userInfo.email;
    body.position_title = userInfo.yourPosition;
    body.clinic_name = userInfo.clinicName;
    body.phone_number = userInfo.phoneNumber;
    if(userInfo.country) {
      body.country = userInfo.country;
    }
    if(country) {
      body.country = country.id
    }

    const { data } = yield call(User.updateUserInfo, body);
    yield put(updateUserInfo.success(data));
  } catch (error) {
    yield put(processRequestError({ error, failAction: updateUserInfo.fail }));
  }
}

function* updateUserPasswordSaga() {
  try {
    const { userInfo } = yield select((state: RootState) => state.user);
    const body: any = {};
    body.old_password = userInfo.currentPassword;
    body.new_password = userInfo.newPassword;
    body.password_confirm = userInfo.passwordConfirm;

    const { data } = yield call(User.updateUserPassword, body);

    // @ts-ignore
    yield put(setNotification(true, t(texts.updateUserData.passwordUpdate)));
    yield put(updateUserPassword.success(data));
  } catch (error) {
    yield put(processRequestError({ error, failAction: updateUserPassword.fail }));
  }
}
function* setCountrySaga() {
  try {
    const { currentCountry } = yield select((state: RootState) => state.user);
    const country = currentCountry.toLowerCase();
    switch (country) {
      case 'austria':
        yield put(setCurrency('aud'));
        break;
      case 'belgium':
      case 'bulgaria':
      case 'croatia':
      case 'cyprus':
      case 'czech republic':
      case 'denmark':
      case 'estonia':
      case 'finland':
      case 'france':
      case 'germany':
      case 'greece':
      case 'ireland':
      case 'italy':
      case 'latvia':
      case 'lithuania':
      case 'luxembourg':
      case 'malta':
      case 'netherlands':
      case 'poland':
      case 'portugal':
      case 'romania':
      case 'slovakia':
      case 'slovenia':
      case 'spain':
      case 'sweden':
        yield put(setCurrency('eur'));
        break;
      case 'united kingdom':
        yield put(setCurrency('gbp'));
        break;
      default:
        break;
    }
    yield put(setIsLocationCheck(true));
  } catch (error) {
    yield put(processRequestError({ error, failAction: updateUserPassword.fail }));
  }
}

function* getCreditCardInfoSaga() {
  try {
    const { data } = yield call(User.getCreditCardInfo);
    yield put(getCreditCardInfo.success(data));
  } catch (error) {
    yield put(getCreditCardInfo.fail(error));
  }
}

function* createCreditCardInfoSaga({
  payload,
}: ActionType<typeof createCreditCardInfo.request>): SagaIterator {
  try {
    const { data } = yield call(User.createCreditCardInfo, payload);
    yield put(createCreditCardInfo.success(data));
    // @ts-ignore
    yield put(setNotification(true, t(texts.updateUserData.cardAdded)));
    yield put(getCreditCardInfo.request());
  } catch (error) {
    yield put(createCreditCardInfo.fail(error));
  }
}

function* deleteCreditCardInfoSaga({
  payload,
}: ActionType<typeof deleteCreditCardInfo.request>): SagaIterator {
  try {
    const { data } = yield call(User.deleteCreditCardInfo, payload);
    yield put(deleteCreditCardInfo.success(data));
    // @ts-ignore
    yield put(setNotification(true, t(texts.updateUserData.cardDelete)));
    yield put(getCreditCardInfo.request());
  } catch (error) {
    yield put(deleteCreditCardInfo.fail(error));
  }
}

function* getClickCountTotalSaga() {
  try {
    const { data } = yield call(User.getTotalClickCount);
    yield put(getClickCountTotal.success(data.clicks_count));
  } catch (error) {
    yield put(getClickCountTotal.fail(error));
  }
}

function* getCountriesSaga() {
  try {
    const { data } = yield call(User.getCountriesList);
    yield put(getCountries.success(data));
  } catch (error) {
    yield put(getCountries.fail(error));
  }
}

export function* watchUser(): SagaIterator {
  yield takeLatest(getUserInfo.request, getUserInfoSaga);
  yield takeLatest(updateUserInfo.request, updateUserInfoSaga);
  yield takeLatest(updateUserPassword.request, updateUserPasswordSaga);
  yield takeLatest(setCountry, setCountrySaga);
  yield takeLatest(getCreditCardInfo.request, getCreditCardInfoSaga);
  yield takeLatest(createCreditCardInfo.request, createCreditCardInfoSaga);
  yield takeLatest(deleteCreditCardInfo.request, deleteCreditCardInfoSaga);
  yield takeLatest(getClickCountTotal.request, getClickCountTotalSaga);
  yield takeLatest(getCountries.request, getCountriesSaga);
}
